/**
 * The recent-errors log of "Report a problem" (beta builds only; docs/inbox.md 2c, docs/plans/error-reports.md): the
 * last few errors as a step name (ERROR_STEPS) and the error's listed type (ERROR_CODES, else "other"). The error is
 * reduced to these two words the moment it is noted and never kept: no message, stack, file name or value. In memory
 * only, gone with the page. Error messages, which the first spec named, are left out on purpose: a message can carry a
 * file name or a value (Matt, 2026-10-22 12:44: nothing such information could be inferred from).
 */
import { ERROR_CODES, ERROR_STEPS, errorCode, type ErrorCode, type ErrorStep } from '../diagnostics/errorCodes';

export interface RecentError {
  step: ErrorStep;
  code: ErrorCode;
}

/** How many of the latest errors the report names. */
export const RECENT_ERRORS_MAX = 5;

const log: RecentError[] = [];

/** Notes an error: only its step and listed type are kept. */
export function noteRecentError(step: ErrorStep, err: unknown): void {
  let code: ErrorCode = 'other';
  try {
    code = errorCode(err);
  } catch {
    // A hostile `name` getter: "other".
  }
  log.push({ step: (ERROR_STEPS as readonly string[]).includes(step) ? step : 'ui', code });
  if (log.length > RECENT_ERRORS_MAX) log.splice(0, log.length - RECENT_ERRORS_MAX);
}

/** The latest errors, oldest first (a copy). */
export const recentErrors = (): RecentError[] => log.map((e) => ({ ...e }));

/** Tests only. */
export const clearRecentErrors = (): void => {
  log.length = 0;
};

export const isRecentError = (e: RecentError): boolean =>
  Object.keys(e).length === 2 && (ERROR_STEPS as readonly string[]).includes(e.step) && (ERROR_CODES as readonly string[]).includes(e.code);
